// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The machine being edited, and what the pointer is doing to it (M9-01, 11 §3.1).
//
// A draft is a `MachineDefinition` plus three things a definition has no business carrying: what is
// selected, what is being dragged, and what the machine looked like before. Keeping them apart is
// the point — the definition in a draft is always a real definition, publishable as it stands, and
// never a half-finished one with a device at a pointer's coordinates mid-gesture.
//
// **A drag is a proposal until it is dropped.** `dragTo` does not touch the definition; it records
// where the pointer is and what the snap rules made of it, and the canvas draws that as a preview.
// `endDrag` is the only thing that writes. This is what makes an escaped or abandoned drag free:
// there is nothing to undo, because nothing happened.
//
// **Undo is per gesture, not per frame.** A drag across a canvas is a hundred pointer events and
// exactly one thing an author did, and a history that recorded all hundred would make Undo a key
// they hold down. Pushed by the operations that write, which are few and named.
//
// **Everything is immutable.** A draft in, a draft out, and React re-renders because the object
// changed — no mutation, no equality trap where a device moved and nothing repainted.

import {type EditorPart as MachineDevice,deviceAnchors,devicePosition,moveDevice,withAnchor} from './parts'
import type {
  MachineDefinition,
  MachineLayer,
  Point2,
  Point3,
} from '../../core/ts/machine'
import {    } from '../../core/ts/machine'
import { snapPoint, type SnapOptions, type SnapResult } from './snapping'
import { wired, wiringFor } from './palette'
import {editableParts,storeLamp} from './parts'
import {targetBackingForEditing} from './targetBacking'
import { layerRemovalRefusal, withoutLayer } from './layerRemoval'
import { withoutDevice } from './deviceRemoval'

/** How many gestures can be taken back. */
export const HISTORY_DEPTH = 50

/** The handle a gesture has hold of. */
export interface Grab {
  readonly deviceId: string
  readonly role: string
  /** Where that handle was when the gesture began, in playfield metres. */
  readonly from: Point2
  /**
   * The offset between the handle and the pointer at the moment of the grab.
   *
   * Kept so a device does not jump: an author who takes hold of a flipper 3 mm off its pivot is
   * still holding it 3 mm off its pivot when they let go. Without it, the first pointer move
   * teleports the handle under the cursor, which reads as the canvas having lost the drag.
   */
  readonly offset: Point2
  /** Whether this handle reshapes the device or moves the whole of it. See `DeviceAnchor.shapes`. */
  readonly shapes: boolean
}

/** The other end of the device in hand, in a second hand (M13-14). */
export interface OtherEnd {
  readonly grab: Grab
  readonly snap: SnapResult
}

/** An end a hand has let go of within this gesture, where it left it (M13-14). */
export interface SettledEnd {
  readonly role: string
  readonly at: Point2
}

/** A drag in flight: where it would land, and why there. */
export interface DragPreview {
  readonly grab: Grab
  readonly snap: SnapResult
  /**
   * The other end of a two-ended device, held by a second hand — or nothing (M13-14).
   *
   * The recommendation's one two-handed gesture: pinch both ends of a slingshot, a target, a
   * spinner or a gate and the rubber follows both hands — turned, lengthened, or both — as *one*
   * thing the author did. Stated on the drag rather than as a second drag because that is what it
   * is: one device, one gesture, one entry in the history.
   */
  readonly other: OtherEnd | undefined
  /**
   * Ends a hand has already let go of within this gesture, still where it left them.
   *
   * Nothing is written until the last hand opens — the rule at the top of this file — so an end
   * the second hand has released is carried here, applied under the same gesture, and committed
   * with the rest by `endDrag` or dropped with the rest by `cancelDrag`. A release over the board
   * settles an end; one in the air sends it back where the gesture found it.
   */
  readonly settled: readonly SettledEnd[]
}

export interface LayoutDraft {
  readonly definition: MachineDefinition
  /**
   * Always present, sometimes nothing.
   *
   * Stated as `| undefined` rather than as an optional member because "nothing is selected" is a
   * state a draft is genuinely in, not a field it happens to lack — and because under
   * `exactOptionalPropertyTypes` the difference between an absent key and an undefined one is one
   * every caller would otherwise have to think about on every update.
   */
  readonly selected: string | undefined
  /**
   * The ramp or habitrail being shaped, if any (M9-04).
   *
   * Held apart from `selected` rather than folded into it. A device and a layer are different things
   * with different inspectors and different handles, and one field that could hold either would put
   * a `kind` check in front of every read — including the ones that only ever meant a device, which
   * is most of them.
   */
  readonly selectedLayer: string | undefined
  readonly drag: DragPreview | undefined
  /**
   * What the last change was part of, for coalescing.
   *
   * A slider dragged across its range is one thing the author did and a hundred changes; a history
   * that recorded all hundred would make Undo a key they hold down — the same reason a canvas drag
   * is one entry. Anything that is *not* a run of edits to one field clears it, so an undo never
   * swallows a change the author made separately.
   */
  readonly gesture: string | undefined
  /** Definitions before each of the last gestures, newest first. */
  readonly past: readonly MachineDefinition[]
  readonly future: readonly MachineDefinition[]
}

/** Upgrade editable content as one undoable gesture; the input/published definition remains untouched. */
export function openDraftForEditing(definition: MachineDefinition): LayoutDraft {
  const draft=openDraft(definition),next=targetBackingForEditing(definition)
  return next===definition?draft:{...draft,definition:next,past:[definition]}
}

export function openDraft(definition: MachineDefinition): LayoutDraft {
  return {
    definition,
    selected: undefined,
    selectedLayer: undefined,
    drag: undefined,
    gesture: undefined,
    past: [],
    future: [],
  }
}

/** The devices, which is the list every canvas operation works over. */
export function devicesOf(draft: LayoutDraft): readonly MachineDevice[] {
  return editableParts(draft.definition)
}

function deviceById(draft: LayoutDraft, id: string): MachineDevice | undefined {
  return devicesOf(draft).find((device) => device.id === id)
}

/**
 * Replace one device, keeping the definition's every other member.
 *
 * Spread rather than rebuilt field by field: a definition has thirty top-level members and a
 * rebuild is a list that silently drops whichever one is added next.
 */
function withDevice(definition: MachineDefinition, next: MachineDevice): MachineDefinition {
  if(next.kind==='flasher')return storeLamp(definition,next)
  const before = (definition.devices ?? []).find((device) => device.id === next.id)
  const devices = (definition.devices ?? []).map((device) => (device.id === next.id ? next : device))
  return { ...(before === undefined ? definition : carryAttachments(definition, before, next)), devices }
}

/** How near two points are to be the same point, playfield metres: a machine file's own rounding. */
export const ATTACHED_WITHIN = 1e-6

/**
 * What a device carries when it moves (the 1.0.104 headset round, 27 Sep 2026: "leaves a
 * partial component copy in the original place"). A machine draws some of a device from outside
 * the device:
 * - **its insert**: the lamp its `lamp` names, painted into the playfield;
 * - **its body**: a collider polyline or segment that runs through the device's own handles,
 *   as a slingshot's plastic runs from one end of its rubber to the other.
 *
 * Moving the device alone left both where they were. They are moved with it here:
 * - The named lamp moves by the device's own displacement.
 * - A collider point on one of the device's handles follows that handle.
 * - When the whole device moved by one offset, the whole collider moves by it, corners and all.
 *
 * - **Its own lamps** (the 1.0.113 round, 27 Sep 2026): every lamp whose id or group is the
 *   device's id and then a word — `darkMatterRung1`…`5` in the group `darkMatterLadder`,
 *   `kickbackArrow`, `ionBankArrow` — moves with it too. darkMatter's five ladder rungs stayed
 *   behind when the captive ball was carried, because only the arrow was named (`lampOwnedBy`).
 *
 * Nothing else is guessed at. A lamp the device neither names nor owns by name, or a wall that
 * merely passes near it, stays put.
 */
export function carryAttachments(
  definition: MachineDefinition,
  before: MachineDevice,
  after: MachineDevice,
): MachineDefinition {
  const from = deviceAnchors(before)
  const to = deviceAnchors(after)
  const moves: { readonly at: Point2; readonly to: Point2 }[] = []
  for (const anchor of from) {
    const now = to.find((a) => a.role === anchor.role)
    if (now === undefined) continue
    if (now.at[0] === anchor.at[0] && now.at[1] === anchor.at[1]) continue
    moves.push({ at: anchor.at, to: now.at })
  }
  const p0 = devicePosition(before)
  const p1 = devicePosition(after)
  const shift: Point2 | undefined = p0 === undefined || p1 === undefined ? undefined : [p1[0] - p0[0], p1[1] - p0[1]]
  if (moves.length === 0 && (shift === undefined || (shift[0] === 0 && shift[1] === 0))) return definition
  // One offset for every handle: the device was carried, not reshaped.
  const whole =
    shift !== undefined &&
    moves.length === from.length &&
    // Within the Builder's own tidying (four places), which rounds each handle's move on its own.
    moves.every((m) => Math.abs(m.to[0] - m.at[0] - shift[0]) <= 1e-4 && Math.abs(m.to[1] - m.at[1] - shift[1]) <= 1e-4)
  const on = (p: Point2): Point2 | undefined => {
    for (const m of moves) {
      if (Math.abs(p[0] - m.at[0]) <= ATTACHED_WITHIN && Math.abs(p[1] - m.at[1]) <= ATTACHED_WITHIN) return m.to
    }
    return undefined
  }
  const record = definition as unknown as Record<string, unknown>
  let changed = false
  let colliders = definition.colliders
  if (Array.isArray(colliders) && moves.length > 0) {
    let touched = false
    const mapped = colliders.map((collider) => {
      const c = collider as unknown as Record<string, unknown>
      const points: Point2[] | undefined =
        collider.kind === 'polyline'
          ? (c['points'] as Point2[])
          : collider.kind === 'segment'
            ? [c['a'] as Point2, c['b'] as Point2]
            : undefined
      if (points === undefined || !points.some((p) => on(p) !== undefined)) return collider
      // A point on a handle lands exactly on it, so the next move finds it again; the rest of a
      // wholly carried body moves by the device's offset.
      const moved = points.map((p): Point2 =>
        on(p) ?? (whole && shift !== undefined ? [p[0] + shift[0], p[1] + shift[1]] : p),
      )
      touched = true
      return (collider.kind === 'polyline' ? { ...c, points: moved } : { ...c, a: moved[0], b: moved[1] }) as unknown as typeof collider
    })
    // The list is replaced only when something on it moved: identity is how the picture knows.
    if (touched) {
      colliders = mapped
      changed = true
    }
  }
  let lamps = record['lamps']
  const lampId = (before as unknown as Record<string, unknown>)['lamp']
  if (Array.isArray(lamps) && shift !== undefined && (shift[0] !== 0 || shift[1] !== 0)) {
    let touched = false
    const mapped = (lamps as Record<string, unknown>[]).map((lamp) => {
      const position = lamp['position']
      if (!lampOwnedBy(before.id, typeof lampId === 'string' ? lampId : undefined, lamp) || !Array.isArray(position)) return lamp
      touched = true
      return { ...lamp, position: [(position[0] as number) + shift[0], (position[1] as number) + shift[1]] }
    })
    if (touched) {
      lamps = mapped
      changed = true
    }
  }
  if (!changed) return definition
  return { ...record, colliders, ...(lamps === undefined ? {} : { lamps }) } as unknown as MachineDefinition
}

/**
 * Whether a lamp goes where a device goes (the 1.0.113 round): the one the device names, or one
 * whose id or group is the device's id followed by a capital — `darkMatterRung3` and the group
 * `darkMatterLadder` for `darkMatter`, never `pulsar10` for `pulsar1` or `laneNorth` for `lane`
 * unless it is capitalised as a word of its own. The flagships name their lamps this way.
 */
export function lampOwnedBy(deviceId: string, namedLamp: string | undefined, lamp: Record<string, unknown>): boolean {
  const id = lamp['id']
  if (namedLamp !== undefined && id === namedLamp) return true
  const owned = (key: unknown): boolean =>
    typeof key === 'string' &&
    key.length > deviceId.length &&
    key.startsWith(deviceId) &&
    /[A-Z]/u.test(key.charAt(deviceId.length))
  return owned(id) || owned(lamp['group'])
}

/**
 * Record a gesture and its result. The only path that writes a definition.
 *
 * `gesture` names what this change is part of. Two changes in a row carrying the same name are one
 * entry in the history — a slider being dragged — and anything else pushes a new one.
 */
function commit(draft: LayoutDraft, definition: MachineDefinition, gesture?: string): LayoutDraft {
  if (definition === draft.definition) return draft
  const sameGesture = gesture !== undefined && gesture === draft.gesture
  return {
    definition,
    selected: draft.selected,
    selectedLayer: draft.selectedLayer,
    drag: draft.drag,
    gesture,
    // The previous state is kept once per gesture: continuing one replaces nothing, because the
    // entry already on the stack is the state before the gesture began.
    past: sameGesture ? draft.past : [draft.definition, ...draft.past].slice(0, HISTORY_DEPTH),
    // **Cleared, not kept.** A redo stack that survived a new edit would offer to reapply a change
    // made to a machine that no longer exists, which is the one way an undo system can lose work
    // rather than restore it.
    future: [],
  }
}

/**
 * Put a new device on the playfield and select it (M9-02).
 *
 * **Selected, because placing a thing is choosing it.** The author's next act is nearly always to
 * move it or to look at its numbers, and a placement that left the previous selection standing
 * would send the first arrow-key press to the wrong device.
 *
 * The device is built by the palette, which owns what each kind is made of; this owns only *when*
 * the definition changes, which is here and once.
 */
/**
 * Change one of a device's numbers (M9-03).
 *
 * The change itself comes from `fields.ts`, which owns what each member is allowed to be; this owns
 * when the definition changes and what counts as one gesture. `gesture` is the device and the field
 * together, so moving from one slider to the next is a new entry in the history and dragging one
 * slider is not.
 */
export function editDevice(
  draft: LayoutDraft,
  deviceId: string,
  change: (device: MachineDevice) => MachineDevice,
  gesture: string,
): LayoutDraft {
  const device = deviceById(draft, deviceId)
  if (device === undefined) return draft
  const next = change(device)
  if (next === device) return draft
  return commit(draft, withDevice(draft.definition, next), gesture)
}

export function place(draft: LayoutDraft, device: MachineDevice): LayoutDraft {
  if(device.kind==='flasher')return {...commit(draft,storeLamp(draft.definition,device)),selected:device.id,drag:undefined}
  // **The wiring comes with it** (M9-09). A device with no switch is a device no rule can ever
  // mention, and an author who places a drop target and finds nothing to point a rule at has been
  // handed two halves of a machine that do not know about each other.
  const wiring = wiringFor(device)
  const record = draft.definition as unknown as Record<string, unknown>
  const switches = Array.isArray(record['switches']) ? (record['switches'] as unknown[]) : []
  const coils = Array.isArray(record['coils']) ? (record['coils'] as unknown[]) : []
  const placed = commit(draft, {
    ...record,
    devices: [...draft.definition.devices, wired(device)],
    ...(wiring.switches.length > 0 ? { switches: [...switches, ...wiring.switches] } : {}),
    ...(wiring.coils.length > 0 ? { coils: [...coils, ...wiring.coils] } : {}),
    ...(wiring.rules?.length ? { rules: { ...draft.definition.rules, rules: [...(draft.definition.rules?.rules ?? []), ...wiring.rules] } } : {}),
  } as unknown as MachineDefinition)
  return { ...placed, selected: device.id, drag: undefined }
}

/**
 * Take a device off the playfield (M9-02).
 *
 * **A palette that only adds is a trap**, which is why this is here rather than waiting for the
 * inspector: the first mistaken placement would otherwise be permanent for a whole milestone.
 *
 * Everything that belonged to the device goes with it — its switches and coil wherever they are
 * named, the rules only its switches triggered, the actions that drove it, its shots, its bank
 * membership and its own colliders (`deviceRemoval.ts`). Leaving any of them behind leaves a machine
 * the validator refuses, with the fault in a record the author never touched — and the headset,
 * which reloads the machine on every edit, cannot take such a part off at all (24 Oct 2026).
 */
export function remove(draft: LayoutDraft, deviceId: string): LayoutDraft {
  const going = devicesOf(draft).find((device) => device.id === deviceId)
  if (going === undefined) return draft
  if(going.kind==='flasher')return {...commit(draft,storeLamp(draft.definition,going,true)),selected:undefined,drag:undefined}
  const gone = commit(draft, withoutDevice(draft.definition, deviceId).definition)
  return { ...gone, selected: undefined, drag: undefined }
}

/**
 * Replace the whole definition, as one gesture (M9-08).
 *
 * The rules workspace edits a part of the machine that has no place on the canvas, so it hands back
 * a whole definition rather than a device or a layer. Everything else about a change is the same —
 * the history, the coalescing — and lives here, which is why this is a third door into `commit`
 * rather than a second history.
 */
export function editWholeDefinition(
  draft: LayoutDraft,
  definition: MachineDefinition,
  gesture: string,
): LayoutDraft {
  return commit(draft, definition, gesture)
}

export function select(draft: LayoutDraft, deviceId: string | undefined): LayoutDraft {
  if (deviceId === draft.selected) return draft
  // Selecting a device puts the layer down. Two things selected at once is two inspectors, and an
  // author who dragged a control point would not know which of them they were about to change.
  return { ...draft, selected: deviceId, selectedLayer: undefined }
}

/**
 * Which control point of the selected ramp the pointer is over, or nothing (M9-04).
 *
 * The same nearest-within-tolerance rule the device handles use, over the selected layer only. Every
 * ramp's points at once would make a busy machine's canvas a field of handles, and only one ramp is
 * being shaped at a time.
 */
export function pointAt(
  spline: readonly Point3[],
  at: Point2,
  toleranceMetres: number,
): number | undefined {
  let best: number | undefined
  let bestGap = toleranceMetres * toleranceMetres
  for (let index = 0; index < spline.length; index++) {
    const point = spline[index]
    if (point === undefined) continue
    const dx = point[0] - at[0]
    const dy = point[1] - at[1]
    const gap = dx * dx + dy * dy
    if (gap > bestGap) continue
    bestGap = gap
    best = index
  }
  return best
}

/** The ramps and habitrails: the layers that are a curve rather than a sheet (M9-04). */
export function layersOf(draft: LayoutDraft): readonly MachineLayer[] {
  return draft.definition.layers ?? []
}

export function selectLayer(draft: LayoutDraft, layerId: string | undefined): LayoutDraft {
  if (layerId === draft.selectedLayer) return draft
  return { ...draft, selectedLayer: layerId, selected: undefined, drag: undefined }
}

/**
 * Reshape a ramp or a habitrail (M9-04).
 *
 * The same shape as `editDevice` and for the same reasons: the change is owned by `splines.ts`,
 * which knows what a spline may be; the history is owned here, and a run of edits carrying one
 * gesture name is one entry.
 */
export function editLayer(
  draft: LayoutDraft,
  layerId: string,
  change: (layer: MachineLayer) => MachineLayer,
  gesture: string,
): LayoutDraft {
  const layers = layersOf(draft)
  const existing = layers.find((layer) => layer.id === layerId)
  if (existing === undefined) return draft
  const next = change(existing)
  if (next === existing) return draft
  return commit(
    draft,
    { ...draft.definition, layers: layers.map((layer) => (layer.id === layerId ? next : layer)) },
    gesture,
  )
}

/**
 * Take a ramp or a habitrail off the machine (M9-04 follow-up).
 *
 * Its switches and support posts go with it (`layerRemoval.ts`); a layer something else leads onto
 * or stands on is refused, and the draft comes back unchanged — the Remove control is disabled with
 * `layerRemovalRefusal`'s sentence, so a refused removal is never a surprise. One gesture, so one
 * Undo brings the whole of it back.
 */
export function removeLayer(draft: LayoutDraft, layerId: string): LayoutDraft {
  if (!layersOf(draft).some((layer) => layer.id === layerId)) return draft
  if (layerRemovalRefusal(draft.definition, layerId) !== undefined) return draft
  const gone = commit(draft, withoutLayer(draft.definition, layerId).definition)
  return {
    ...gone,
    selectedLayer: gone.selectedLayer === layerId ? undefined : gone.selectedLayer,
    drag: undefined,
  }
}

/**
 * Close the gesture in flight, so the next change under the same name is a new entry (M13-24).
 *
 * The canvas leaves a gesture open until another edit names a different one, which is why two
 * drags of the same control point in a row are one undo there. The bench closes it at the
 * release: a pinch is a decision, and two pinches are two.
 */
export function endGesture(draft: LayoutDraft): LayoutDraft {
  if (draft.gesture === undefined) return draft
  return { ...draft, gesture: undefined }
}

/** What the pointer is over, or nothing. Nearest anchor within tolerance wins. */
export function anchorAt(
  devices: readonly MachineDevice[],
  point: Point2,
  toleranceMetres: number,
): { readonly deviceId: string; readonly role: string; readonly at: Point2; readonly shapes: boolean } | undefined {
  let best: { deviceId: string; role: string; at: Point2; shapes: boolean } | undefined
  let bestGap = toleranceMetres * toleranceMetres
  for (const device of devices) {
    for (const anchor of deviceAnchors(device)) {
      const dx = anchor.at[0] - point[0]
      const dy = anchor.at[1] - point[1]
      const gap = dx * dx + dy * dy
      if (gap > bestGap) continue
      bestGap = gap
      best = { deviceId: device.id, role: anchor.role, at: anchor.at, shapes: anchor.shapes }
    }
  }
  return best
}

/** A handle by name — the device and the anchor's role — where it is now, or nothing. */
export function anchorOf(
  devices: readonly MachineDevice[],
  deviceId: string,
  role: string,
): { readonly deviceId: string; readonly role: string; readonly at: Point2; readonly shapes: boolean } | undefined {
  for (const device of devices) {
    if (device.id !== deviceId) continue
    for (const anchor of deviceAnchors(device)) {
      if (anchor.role === role) return { deviceId, role, at: anchor.at, shapes: anchor.shapes }
    }
  }
  return undefined
}

/**
 * Take hold of a handle.
 *
 * Selects the device as a side effect, because grabbing a thing is how a pointer selects it and an
 * author who drags an unselected device and finds the inspector still showing the last one has been
 * shown two different answers to "what am I working on".
 */
export function beginDrag(
  draft: LayoutDraft,
  at: Point2,
  toleranceMetres: number,
  /**
   * How near the pointer must be to a handle to take it — by default the snap tolerance, which
   * is the canvas's one number for both. A hand is not a mouse: a fingertip in the air needs a
   * wider catch than a snap should have, or the part it reaches for is "not there" (M13-14), so
   * the headset passes the two separately and the snap stays as tight as it was.
   */
  catchMetres: number = toleranceMetres,
): LayoutDraft {
  const found = anchorAt(devicesOf(draft), at, catchMetres)
  if (found === undefined) return select(draft, undefined)
  return beginDragOn(draft, found, at, toleranceMetres)
}

/**
 * Take hold of a handle already chosen — the one lit under a hand on the bench — with the grab's
 * offset measured from where the pinch actually closed, so the part does not jump to the fingers.
 */
export function beginDragOn(
  draft: LayoutDraft,
  found: { readonly deviceId: string; readonly role: string; readonly at: Point2; readonly shapes: boolean },
  at: Point2,
  toleranceMetres: number,
): LayoutDraft {
  const grab: Grab = {
    deviceId: found.deviceId,
    role: found.role,
    from: found.at,
    offset: [found.at[0] - at[0], found.at[1] - at[1]],
    shapes: found.shapes,
  }
  return {
    ...draft,
    selected: found.deviceId,
    drag: {
      grab,
      snap: snapPoint(found.at, snapAgainst(draft, found.deviceId, toleranceMetres)),
      other: undefined,
      settled: [],
    },
  }
}

/**
 * Everything the drag may align to: every device but the one in hand.
 *
 * **Excluding itself is not a detail.** A device is always exactly on its own anchors, so a drag
 * that could align to them would be caught by its own starting position for the whole of the
 * gesture and never move at all.
 */
export function snapAgainst(
  draft: LayoutDraft,
  movingId: string,
  toleranceMetres: number,
  symmetry = true,
): SnapOptions {
  return {
    devices: devicesOf(draft).filter((device) => device.id !== movingId),
    toleranceMetres,
    symmetry,
  }
}

/**
 * The pointer moved. Answers where the handle would land and why.
 *
 * Nothing is written. See the note at the top of this file.
 */
export function dragTo(draft: LayoutDraft, pointer: Point2, options: SnapOptions): LayoutDraft {
  const drag = draft.drag
  if (drag === undefined) return draft
  const wanted: Point2 = [pointer[0] + drag.grab.offset[0], pointer[1] + drag.grab.offset[1]]
  return { ...draft, drag: { ...drag, snap: snapPoint(wanted, options) } }
}

/**
 * A second hand takes the other end of the device in hand (M13-14).
 *
 * Only the device already in hand, only an end that shapes it, only an end the first hand does
 * not hold, and only within `catchMetres` of where that end is *now* — settled where a hand left
 * it earlier in this gesture, or where the definition has it. A second pinch anywhere else is the
 * second hand choosing nothing, and the drag in flight is left exactly as it was, selection
 * included: a pinch that misses the other end must not drop the part the first hand is holding,
 * which is what `beginDrag`'s "nothing here clears the selection" would do.
 *
 * An end taken back out of `settled` is held from where it was settled, so a hand that let go of
 * a rubber's end to re-grip it finds the end where it left it.
 */
export function beginOtherDrag(
  draft: LayoutDraft,
  at: Point2,
  toleranceMetres: number,
  catchMetres: number = toleranceMetres,
): LayoutDraft {
  const drag = draft.drag
  if (drag === undefined || !drag.grab.shapes || drag.other !== undefined) return draft
  const shown = previewOf(draft)
  if (shown === undefined) return draft
  let best: { role: string; at: Point2 } | undefined
  let bestGap = catchMetres * catchMetres
  for (const anchor of deviceAnchors(shown)) {
    if (!anchor.shapes || anchor.role === drag.grab.role) continue
    const dx = anchor.at[0] - at[0]
    const dy = anchor.at[1] - at[1]
    const gap = dx * dx + dy * dy
    if (gap > bestGap) continue
    bestGap = gap
    best = { role: anchor.role, at: anchor.at }
  }
  if (best === undefined) return draft
  const grab: Grab = {
    deviceId: drag.grab.deviceId,
    role: best.role,
    from: best.at,
    offset: [best.at[0] - at[0], best.at[1] - at[1]],
    shapes: true,
  }
  const role = best.role
  return {
    ...draft,
    drag: {
      ...drag,
      other: { grab, snap: snapPoint(best.at, snapAgainst(draft, drag.grab.deviceId, toleranceMetres)) },
      settled: drag.settled.filter((end) => end.role !== role),
    },
  }
}

/** The second hand moved (M13-14). `dragTo` for the other end; nothing is written. */
export function dragOtherTo(draft: LayoutDraft, pointer: Point2, options: SnapOptions): LayoutDraft {
  const drag = draft.drag
  if (drag === undefined || drag.other === undefined) return draft
  const other = drag.other
  const wanted: Point2 = [pointer[0] + other.grab.offset[0], pointer[1] + other.grab.offset[1]]
  return { ...draft, drag: { ...drag, other: { grab: other.grab, snap: snapPoint(wanted, options) } } }
}

/**
 * One of two hands let go, and the other still holds (M13-14).
 *
 * The end that was released is settled where it was — kept on the drag, not written — when the
 * hand opened over the board, and sent back where the gesture found it otherwise. When it is the
 * *first* hand that let go, the second hand's end becomes the one the gesture is carried by, so
 * the author can pass a rubber from hand to hand without ever putting it down. The last hand to
 * open still decides the whole gesture: `endDrag` writes every end as one entry, `cancelDrag`
 * writes none of them.
 *
 * Nothing to do when only one hand holds: that release is `endDrag` or `cancelDrag`, as before.
 */
export function releaseEnd(draft: LayoutDraft, which: 'first' | 'other', over: boolean): LayoutDraft {
  const drag = draft.drag
  if (drag === undefined || drag.other === undefined) return draft
  const leaving = which === 'other' ? drag.other : { grab: drag.grab, snap: drag.snap }
  const staying = which === 'other' ? { grab: drag.grab, snap: drag.snap } : drag.other
  const settled = over ? [...drag.settled, { role: leaving.grab.role, at: leaving.snap.at }] : drag.settled
  return { ...draft, drag: { grab: staying.grab, snap: staying.snap, other: undefined, settled } }
}

/**
 * The device in hand, as it would be if the drag ended now.
 *
 * A handle that `shapes` moves alone — one end of a rubber, one end of a gate — and one that does
 * not carries the whole device with it, so a flipper dragged by its pivot keeps its length and its
 * angle. Which of the two it is was decided by 04's own vocabulary, back in `deviceAnchors`.
 *
 * **The canvas draws this and `endDrag` writes it**, which is what makes a drop invisible: the
 * device does not jump on release, because what was under the pointer *was* the answer. Two
 * computations of that — one to preview and one to commit — is exactly where a canvas acquires a
 * one-pixel disagreement nobody can reproduce.
 */
export function previewOf(draft: LayoutDraft): MachineDevice | undefined {
  const drag = draft.drag
  if (drag === undefined) return undefined
  let device = deviceById(draft, drag.grab.deviceId)
  if (device === undefined) return undefined
  // The other hand's end, and any end already let go of, go on first (M13-14): two `withAnchor`
  // calls under one gesture, and a gate's `passDirection` follows its face through both — the
  // second call is handed the face the first one made, so the side it kept is kept again.
  for (const end of drag.settled) device = withAnchor(device, end.role, end.at)
  if (drag.other !== undefined) device = withAnchor(device, drag.other.grab.role, drag.other.snap.at)
  const to = drag.snap.at
  return drag.grab.shapes
    ? withAnchor(device, drag.grab.role, to)
    : moveDevice(device, to[0] - drag.grab.from[0], to[1] - drag.grab.from[1])
}

/**
 * The devices as the canvas should draw them: the machine, with the one in hand where it would land.
 *
 * **A device has to follow the pointer, or the drag is a dot moving over a stationary machine.**
 * Found by dragging one: the snap guides were drawn, the readout was right, and the bumper stayed
 * where it started until the mouse came up.
 */
export function drawnDevices(draft: LayoutDraft): readonly MachineDevice[] {
  const preview = previewOf(draft)
  if (preview === undefined) return devicesOf(draft)
  return devicesOf(draft).map((device) => (device.id === preview.id ? preview : device))
}

/** Let go. The preview becomes the machine. */
export function endDrag(draft: LayoutDraft): LayoutDraft {
  const drag = draft.drag
  if (drag === undefined) return draft
  const moved = previewOf(draft)
  if (moved === undefined) return { ...draft, drag: undefined }
  const committed = commit(draft, withDevice(draft.definition, moved))
  return { ...committed, drag: undefined }
}

/** Abandon it. Nothing was written, so there is nothing to take back. */
export function cancelDrag(draft: LayoutDraft): LayoutDraft {
  if (draft.drag === undefined) return draft
  return { ...draft, drag: undefined }
}

/**
 * Nudge the selected device by a whole number of millimetres.
 *
 * The keyboard path, and it is not a convenience: a pointer cannot reliably express "3 mm left" at
 * a zoom where 3 mm is four pixels, and 11 §3.1's grid exists precisely so that exact offsets are
 * expressible. Arrow keys are how a device is placed exactly; the pointer is how it is placed
 * roughly and quickly.
 */
export function nudge(draft: LayoutDraft, dx: number, dy: number): LayoutDraft {
  if (draft.selected === undefined) return draft
  const device = deviceById(draft, draft.selected)
  if (device === undefined) return draft
  return commit(draft, withDevice(draft.definition, moveDevice(device, dx, dy)))
}

export function undo(draft: LayoutDraft): LayoutDraft {
  const [previous, ...rest] = draft.past
  if (previous === undefined) return draft
  return {
    ...draft,
    definition: previous,
    drag: undefined,
    // **Cleared, so the next edit starts a new entry.** Without this, undoing in the middle of a
    // slider drag and then moving the same slider again would coalesce onto the entry that was just
    // taken back, and the undo would appear not to have happened.
    gesture: undefined,
    past: rest,
    future: [draft.definition, ...draft.future].slice(0, HISTORY_DEPTH),
  }
}

export function redo(draft: LayoutDraft): LayoutDraft {
  const [next, ...rest] = draft.future
  if (next === undefined) return draft
  return {
    ...draft,
    definition: next,
    drag: undefined,
    // **Cleared, so the next edit starts a new entry.** Without this, undoing in the middle of a
    // slider drag and then moving the same slider again would coalesce onto the entry that was just
    // taken back, and the undo would appear not to have happened.
    gesture: undefined,
    past: [draft.definition, ...draft.past].slice(0, HISTORY_DEPTH),
    future: rest,
  }
}

export function canUndo(draft: LayoutDraft): boolean {
  return draft.past.length > 0
}

export function canRedo(draft: LayoutDraft): boolean {
  return draft.future.length > 0
}

/**
 * The machine put back to `original` as one step of the history (the 1.0.115 round, 27 Sep 2026:
 * "user should have option … to reset build machine back to original pinball machine"). Undo
 * brings the work back; the redo stack goes, as it does for any edit; nothing is selected, since
 * the selected part may not exist on the original. The same draft when it already is the original.
 */
export function resetTo(draft: LayoutDraft, original: MachineDefinition): LayoutDraft {
  if (original === draft.definition) return draft
  return { ...commit(draft, original), selected: undefined, selectedLayer: undefined, drag: undefined, gesture: undefined }
}

/**
 * The parts and layers that differ between two definitions, by id: changed, added or taken off,
 * devices first. What an Undo or a Redo row names ("Undo · flipper1").
 */
export function changedIds(before: MachineDefinition, after: MachineDefinition): readonly string[] {
  if (before === after) return []
  const ids: string[] = []
  const compare = (a: readonly { readonly id: string }[], b: readonly { readonly id: string }[]): void => {
    const was = new Map(a.map((item) => [item.id, item]))
    const now = new Map(b.map((item) => [item.id, item]))
    for (const [id, item] of now) {
      const old = was.get(id)
      if (old === undefined || (old !== item && JSON.stringify(old) !== JSON.stringify(item))) ids.push(id)
    }
    for (const id of was.keys()) if (!now.has(id)) ids.push(id)
  }
  compare(editableParts(before), editableParts(after))
  compare(before.layers, after.layers)
  return ids
}

/** What the next Undo would take back, by id; empty when there is nothing to undo. */
export function undoIds(draft: LayoutDraft): readonly string[] {
  const previous = draft.past[0]
  return previous === undefined ? [] : changedIds(previous, draft.definition)
}

/** What the next Redo would put back, by id; empty when there is nothing to redo. */
export function redoIds(draft: LayoutDraft): readonly string[] {
  const next = draft.future[0]
  return next === undefined ? [] : changedIds(draft.definition, next)
}
